import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { createApiContext } from "../../../shared/api/create-api-context";
import type { Protocol, ProtocolInput, ProtocolsApi, ProtocolTimelineRow } from "./protocols-api";

const context = createApiContext<ProtocolsApi>("Protocols");
export const ProtocolsApiProvider = context.Provider;
export const useProtocolsApi = context.useApi;

export const protocolKeys = {
  all: (profileId: string) => ["protocols", profileId] as const,
  list: (profileId: string, page: number) =>
    [...protocolKeys.all(profileId), "list", page] as const,
  timeline: (profileId: string) => [...protocolKeys.all(profileId), "timeline"] as const,
};

export function useProtocols(profileId: string, page = 0): UseQueryResult<Protocol[]> {
  const api = useProtocolsApi();
  return useQuery({
    queryKey: protocolKeys.list(profileId, page),
    queryFn: ({ signal }) => api.list(profileId, page, signal),
    enabled: profileId !== "",
  });
}

/** `enabled` permite omitir a sobreposição quando o módulo de protocolos está desligado. */
export function useProtocolTimeline(
  profileId: string,
  enabled = true,
): UseQueryResult<ProtocolTimelineRow[]> {
  const api = useProtocolsApi();
  return useQuery({
    queryKey: protocolKeys.timeline(profileId),
    queryFn: ({ signal }) => api.listForTimeline(profileId, signal),
    enabled: enabled && profileId !== "",
  });
}

export function useSaveProtocol(userId: string, profileId: string, known: Protocol[]) {
  const api = useProtocolsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProtocolInput) => api.saveProtocol(userId, input, known),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: protocolKeys.all(profileId) });
    },
  });
}

export function useDeleteProtocol(profileId: string) {
  const api = useProtocolsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (protocolId: string) => api.deleteProtocol(protocolId),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: protocolKeys.all(profileId) });
    },
  });
}

export function useActivateProtocol(profileId: string, known: Protocol[]) {
  const api = useProtocolsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (protocolId: string) => api.activateProtocol(protocolId, known),
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: protocolKeys.all(profileId) });
    },
  });
}
